import { useState, useSyncExternalStore } from "react";
import { Database, Download, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
    downloadDatabaseBackup, isDatabaseBackupRunning, subscribeDatabaseBackup,
} from "../lib/databaseBackup";
import { Button } from "./ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";

export default function DatabaseBackupButton() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const running = useSyncExternalStore(subscribeDatabaseBackup, isDatabaseBackupRunning, () => false);
    if (user?.role !== "superadmin" || user?.active === false) return null;

    const handleDownload = async () => {
        if (isDatabaseBackupRunning()) return;
        setOpen(false);
        const toastId = toast.loading("Generando el respaldo completo. Mant\u00e9n esta pesta\u00f1a abierta.");
        try {
            const result = await downloadDatabaseBackup();
            toast.success("Respaldo generado. Se inici\u00f3 la descarga.", {
                id: toastId,
                description: result.consistency === "live"
                    ? "Copia en vivo: si hubo cambios durante el respaldo, rep\u00edtelo sin actividad."
                    : result.filename,
                duration: 8000,
            });
        } catch (error) {
            toast.error(error.message || "No se pudo generar el respaldo.", { id: toastId, duration: 8000 });
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                disabled={running}
                aria-label={running ? "Generando respaldo completo" : "Descargar respaldo completo de la base de datos"}
                aria-busy={running}
                title="Backup completo de la base de datos"
                data-testid="database-backup-button"
                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-slate-700 px-2.5 py-2 text-xs font-medium text-slate-200 transition-colors hover:bg-slate-800 disabled:cursor-wait disabled:opacity-60 md:border-slate-200 md:bg-white md:text-slate-600 md:hover:bg-slate-100 md:hover:text-slate-900"
            >
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                <span className="hidden sm:inline">{running ? "Generando..." : "Backup"}</span>
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-[calc(100vw-2rem)] rounded-lg sm:max-w-lg" data-testid="database-backup-dialog">
                    <DialogHeader>
                        <DialogTitle>Respaldo completo de la base de datos</DialogTitle>
                        <DialogDescription>
                            {"Descarga un ZIP con todas las colecciones, registros e \u00edndices del CRM, incluidos los datos de todos los usuarios. No modifica ni elimina informaci\u00f3n."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />
                        <p>{"El archivo contiene informaci\u00f3n confidencial y no est\u00e1 cifrado. Gu\u00e1rdalo en un lugar privado. No incluye archivos externos ni claves de configuraci\u00f3n del servidor."}</p>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600">
                        {"El servidor generar\u00e1 una copia consistente de PostgreSQL. El ZIP incluye database.dump y las indicaciones para restaurarlo."}
                    </p>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button type="button" onClick={handleDownload} disabled={running} data-testid="database-backup-confirm" className="bg-orange-500 hover:bg-orange-600 text-white">
                            <Download className="h-4 w-4" /> Descargar backup
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
